import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import { isEmpty, omitBy } from 'lodash'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { isValidEmail } from 'modules/core/lib/email'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCandidateSources } from 'modules/ats/hooks/candidateSources/useCandidateSources'
import { useCreateCandidate } from 'modules/ats/hooks/candidates/useCreateCandidate'
import { CandidateSources, getSourceLabel } from 'modules/ats/lib/utils/candidateSources'
import { getGenderOptions } from 'modules/ats/lib/utils/gender'

interface NewCandidateFormValues {
  firstName: string
  lastName: string
  email?: string
  gender?: string
  phoneNumber?: string
  personalUrl?: string
  sourceId: string
}

const NewCandidatePage = observer(() => {
  const { company } = useCurrentSession()
  const { loading: loadingSources, candidateSources } = useCandidateSources()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { create } = useCreateCandidate()

  const candidateSourcesOptions = candidateSources.map((source) => ({
    label: getSourceLabel(source.name),
    value: source.id,
  }))

  if (!company) {
    return null
  }

  const form = useForm<NewCandidateFormValues>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      personalUrl: '',
      phoneNumber: '',
      sourceId:
        candidateSources
          .find((source) => source.name === getSourceLabel(CandidateSources.ManuallyAdded))
          ?.id.toString() || '',
    },
    validate: (data) => {
      const errors: FormErrors<NewCandidateFormValues> = {}

      if (!data.firstName) {
        errors.firstName = i18n.t('common.empty')
      }

      if (!data.lastName) {
        errors.lastName = i18n.t('common.empty')
      }

      if (form.touched.email && data.email && !isValidEmail(data.email, { allowEmpty: true })) {
        errors.email = i18n.t('ats.candidates.candidate_form.invalid_email')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await create({
        ...omitBy(data, isEmpty),
        companyId: company.id,
        firstName: data.firstName,
        lastName: data.lastName,
        sourceId: parseInt(data.sourceId),
      })

      const handled = handleResult(result, ({ firstName, lastName, email }) => {
        setErrors({
          firstName,
          lastName,
          email,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        navigateToCandidates()
      }

      return handled
    },
  })

  const navigateToCandidates = () => navigate(navigation.ats.candidates.fullPath())

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('ats.candidates.candidate_form.title')}
      description={i18n.t('ats.candidates.candidate_form.description')}
      onClose={navigateToCandidates}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button
          disabled={
            loadingSources ||
            form.isSubmitting ||
            !form.values.firstName ||
            !form.values.lastName ||
            !form.isValid
          }
          submit
        >
          {i18n.t('ats.candidates.candidate_form.save')}
        </Button>
      }
    >
      <Box gap="s16">
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('ats.candidates.candidate_form.first_name')}
            value={form.values.firstName}
            onChange={(value) => form.setValues({ ...form.values, firstName: value })}
            error={form.touched.firstName ? form.errors.firstName : undefined}
            mandatory
          />
          <TextField
            label={i18n.t('ats.candidates.candidate_form.last_name')}
            value={form.values.lastName}
            onChange={(value) => form.setValues({ ...form.values, lastName: value })}
            error={form.touched.lastName ? form.errors.lastName : undefined}
            mandatory
          />
        </Box>
        <TextField
          label={i18n.t('ats.candidates.candidate_form.email')}
          value={form.values.email}
          onChange={(value) => form.setValues({ ...form.values, email: value })}
          error={form.touched.email ? form.errors.email : undefined}
        />
        <SelectField
          none={i18n.t('common.select_one')}
          value={form.values.gender}
          label={i18n.t('ats.candidates.candidate_form.gender')}
          options={getGenderOptions()}
          onChange={(value) => form.setFieldValue('gender', value)}
        />
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('ats.candidates.candidate_form.personal_url')}
            value={form.values.personalUrl}
            onChange={(value) => form.setValues({ ...form.values, personalUrl: value })}
          />
          <TextField
            label={i18n.t('ats.candidates.candidate_form.phone_number')}
            value={form.values.phoneNumber}
            onChange={(value) => form.setValues({ ...form.values, phoneNumber: value })}
          />
        </Box>
        <SelectField
          label={i18n.t('ats.hiring_tool.add_candidate.source')}
          options={candidateSourcesOptions}
          onChange={(value) => form.setValues({ ...form.values, sourceId: value })}
          value={form.values.sourceId}
          mandatory
        />

        <Alert>
          <Text.Normal>
            {i18n.t('ats.candidates.candidate_form.data_protection.message')}{' '}
            <a
              href={i18n.t('ats.candidates.candidate_form.data_protection.url')}
              target="_blank"
              rel="noreferrer"
            >
              {i18n.t('ats.candidates.candidate_form.data_protection.link_text')}
            </a>
          </Text.Normal>
        </Alert>
      </Box>
    </Modal>
  )
})

export default NewCandidatePage
